import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Search, Plus, Filter, Edit, Trash2, Copy, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { mockFeedbackSnippets } from '../../../data/mockData';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Label } from '../../ui/label';
import { toast } from 'sonner';

export default function FeedbackLibrary() {
  const [snippets, setSnippets] = useState(mockFeedbackSnippets);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSnippet, setNewSnippet] = useState({ text: '', category: '', sentiment: '', tags: '' });
  const [editingSnippetId, setEditingSnippetId] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('feedbackSnippets');
      if (stored) {
        setSnippets(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load stored snippets', error);
    }
  }, []);

  const filteredSnippets = snippets.filter(snippet => {
    const matchesSearch = searchQuery === '' ||
      snippet.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      snippet.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || snippet.category === categoryFilter;
    const matchesSentiment = sentimentFilter === 'all' || snippet.sentiment === sentimentFilter;
    return matchesSearch && matchesCategory && matchesSentiment;
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'bg-emerald-100 text-emerald-700';
      case 'improvement': return 'bg-amber-100 text-amber-700';
      case 'critical': return 'bg-rose-100 text-rose-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'performance': return 'bg-violet-100 text-violet-700';
      case 'goals': return 'bg-cyan-100 text-cyan-700';
      case 'general': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleAddOrUpdateSnippet = () => {
    if (!newSnippet.text.trim() || !newSnippet.category || !newSnippet.sentiment) {
      toast.error('Text, category, and sentiment are required');
      return;
    }

    const parsedTags = newSnippet.tags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    const snippetToPersist = {
      id: editingSnippetId || uuidv4(),
      text: newSnippet.text.trim(),
      category: newSnippet.category,
      sentiment: newSnippet.sentiment,
      tags: parsedTags
    };

    const updated = editingSnippetId
      ? snippets.map(s => (s.id === editingSnippetId ? snippetToPersist : s))
      : [snippetToPersist, ...snippets];
    setSnippets(updated);
    try {
      localStorage.setItem('feedbackSnippets', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save snippets', error);
    }
    setNewSnippet({ text: '', category: '', sentiment: '', tags: '' });
    setEditingSnippetId(null);
    setShowAddModal(false);
    toast.success(editingSnippetId ? 'Snippet updated' : 'Snippet added');
  };

  const startEditSnippet = (snippet) => {
    setEditingSnippetId(snippet.id);
    setNewSnippet({
      text: snippet.text,
      category: snippet.category,
      sentiment: snippet.sentiment,
      tags: (snippet.tags || []).join(', ')
    });
    setShowAddModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Library</h1>
          <p className="text-gray-500 mt-1">Manage reusable feedback templates</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Snippet
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-bold text-gray-900">{snippets.length}</p>
            <p className="text-sm text-gray-500">Total Snippets</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-bold text-emerald-600">
              {snippets.filter(s => s.sentiment === 'positive').length}
            </p>
            <p className="text-sm text-gray-500">Positive</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-bold text-amber-600">
              {snippets.filter(s => s.sentiment === 'improvement').length}
            </p>
            <p className="text-sm text-gray-500">Improvement</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-2xl font-bold text-gray-600">
              {snippets.filter(s => s.sentiment === 'neutral').length}
            </p>
            <p className="text-sm text-gray-500">Neutral</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search snippets or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="performance">Performance</SelectItem>
                <SelectItem value="goals">Goals</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sentiment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sentiments</SelectItem>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="improvement">Improvement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Snippets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredSnippets.map(snippet => (
          <Card key={snippet.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge className={getCategoryColor(snippet.category)}>{snippet.category}</Badge>
                  <Badge className={getSentimentColor(snippet.sentiment)}>{snippet.sentiment}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => copyToClipboard(snippet.text)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => startEditSnippet(snippet)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-rose-500 hover:text-rose-600">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <p className="text-gray-700 mb-3">{snippet.text}</p>
              {snippet.tags && snippet.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {snippet.tags.map((tag, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredSnippets.length === 0 && (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No feedback snippets found</p>
        </div>
      )}

      {/* Add/Edit Snippet Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSnippetId ? 'Edit Feedback Snippet' : 'Add Feedback Snippet'}</DialogTitle>
            <DialogDescription>Create or update a reusable feedback template</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Feedback Text *</Label>
              <Textarea
                placeholder="Enter your feedback template..."
                value={newSnippet.text}
                onChange={(e) => setNewSnippet({ ...newSnippet, text: e.target.value })}
                className="min-h-24"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={newSnippet.category} onValueChange={(v) => setNewSnippet({ ...newSnippet, category: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="goals">Goals</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sentiment *</Label>
                <Select value={newSnippet.sentiment} onValueChange={(v) => setNewSnippet({ ...newSnippet, sentiment: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positive">Positive</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tags (comma separated)</Label>
              <Input
                placeholder="quality, teamwork, leadership"
                value={newSnippet.tags}
                onChange={(e) => setNewSnippet({ ...newSnippet, tags: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); setEditingSnippetId(null); }}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleAddOrUpdateSnippet}>
              {editingSnippetId ? 'Update Snippet' : 'Add Snippet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
